import { ShieldOff } from 'lucide-react';
import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AdminLayout } from '@/app/layouts/admin-layout';
import { LoginPage, usePermissions } from '@/features/auth';
import { EmptyState } from '@/shared/components/empty-state';
import { NotFoundPage } from '@/shared/components/errors/not-found-page';
import { RouteErrorPage } from '@/shared/components/errors/route-error-page';
import { PageContainer } from '@/shared/components/page-container';

import { landingPath } from '../config/navigation';
import { RequireAuth, RequirePermission } from './guards';

/**
 * The route table.
 *
 * Every business module is lazily loaded so the initial bundle carries only
 * the shell and the login screen (§77). The login page itself is eagerly
 * imported — it is the entry point for unauthenticated users and lazy-loading
 * it would add a round trip to the very first paint.
 *
 * Each module sits behind two gates: `RequireAuth` for a session and the
 * current user, then `RequirePermission` for the specific capability from
 * `/admin/me`. The permission passed here must match the one on the matching
 * entry in `app/config/navigation.ts`, so that a hidden nav item and a blocked
 * route stay consistent.
 */

const DashboardPage = lazy(() =>
  import('@/features/dashboard').then((m) => ({ default: m.DashboardPage })),
);
const BrandsPage = lazy(() => import('@/features/brands').then((m) => ({ default: m.BrandsPage })));
const BrandDetailsPage = lazy(() =>
  import('@/features/brands').then((m) => ({ default: m.BrandDetailsPage })),
);
const CatalogueImportPage = lazy(() =>
  import('@/features/catalogue-import').then((m) => ({ default: m.CatalogueImportPage })),
);
const CategoriesPage = lazy(() =>
  import('@/features/categories').then((m) => ({ default: m.CategoriesPage })),
);
const RegionsPage = lazy(() =>
  import('@/features/regions').then((m) => ({ default: m.RegionsPage })),
);
const MarketPage = lazy(() => import('@/features/market').then((m) => ({ default: m.MarketPage })));
const ProvidersPage = lazy(() =>
  import('@/features/providers').then((m) => ({ default: m.ProvidersPage })),
);
const ProviderDetailsPage = lazy(() =>
  import('@/features/providers').then((m) => ({ default: m.ProviderDetailsPage })),
);
const CurrenciesPage = lazy(() =>
  import('@/features/pricing').then((m) => ({ default: m.CurrenciesPage })),
);
const CountriesPage = lazy(() =>
  import('@/features/pricing').then((m) => ({ default: m.CountriesPage })),
);
const CountryPage = lazy(() =>
  import('@/features/pricing').then((m) => ({ default: m.CountryPage })),
);
const PricingCalculatorPage = lazy(() =>
  import('@/features/pricing').then((m) => ({ default: m.PricingCalculatorPage })),
);
const ReportsPage = lazy(() =>
  import('@/features/reports').then((m) => ({ default: m.ReportsPage })),
);
const PartnerReportPage = lazy(() =>
  import('@/features/reports').then((m) => ({ default: m.PartnerReportPage })),
);
const SettlementsPage = lazy(() =>
  import('@/features/settlements').then((m) => ({ default: m.SettlementsPage })),
);
const NewSettlementPage = lazy(() =>
  import('@/features/settlements').then((m) => ({ default: m.NewSettlementPage })),
);
const SettlementDetailsPage = lazy(() =>
  import('@/features/settlements').then((m) => ({ default: m.SettlementDetailsPage })),
);
const ApprovalsInboxPage = lazy(() =>
  import('@/features/approvals').then((m) => ({ default: m.ApprovalsInboxPage })),
);
const AuditLogPage = lazy(() =>
  import('@/features/audit-log').then((m) => ({ default: m.AuditLogPage })),
);
const PurchaseLimitsPage = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.PurchaseLimitsPage })),
);
const SystemHealthPage = lazy(() =>
  import('@/features/system-health').then((m) => ({ default: m.SystemHealthPage })),
);
const StaffPage = lazy(() => import('@/features/staff').then((m) => ({ default: m.StaffPage })));
const NewsletterSubscribersPage = lazy(() =>
  import('@/features/newsletter').then((m) => ({ default: m.NewsletterSubscribersPage })),
);
const LoyaltyConfigurationPage = lazy(() =>
  import('@/features/loyalty').then((m) => ({ default: m.LoyaltyConfigurationPage })),
);
const CouponsPage = lazy(() =>
  import('@/features/coupons').then((m) => ({ default: m.CouponsPage })),
);
const CampaignsPage = lazy(() =>
  import('@/features/campaigns').then((m) => ({ default: m.CampaignsPage })),
);
const CampaignDetailsPage = lazy(() =>
  import('@/features/campaigns').then((m) => ({ default: m.CampaignDetailsPage })),
);
const CampaignReportPage = lazy(() =>
  import('@/features/campaigns').then((m) => ({ default: m.CampaignReportPage })),
);
const AgenciesPage = lazy(() =>
  import('@/features/campaigns').then((m) => ({ default: m.AgenciesPage })),
);
const GlobalDiscountPage = lazy(() =>
  import('@/features/global-discount').then((m) => ({ default: m.GlobalDiscountPage })),
);
const CustomersPage = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.CustomersPage })),
);
const CustomerPage = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.CustomerPage })),
);
const ComplaintsPage = lazy(() =>
  import('@/features/complaints').then((m) => ({ default: m.ComplaintsPage })),
);
const ComplaintPage = lazy(() =>
  import('@/features/complaints').then((m) => ({ default: m.ComplaintPage })),
);
const RefundsPage = lazy(() =>
  import('@/features/refunds').then((m) => ({ default: m.RefundsPage })),
);
const FulfilmentPage = lazy(() =>
  import('@/features/fulfilment').then((m) => ({ default: m.FulfilmentPage })),
);
const ReconciliationPage = lazy(() =>
  import('@/features/reconciliation').then((m) => ({ default: m.ReconciliationPage })),
);
const OrdersPage = lazy(() => import('@/features/orders').then((m) => ({ default: m.OrdersPage })));
const OrderDetailsPage = lazy(() =>
  import('@/features/orders').then((m) => ({ default: m.OrderDetailsPage })),
);
const ThemePreviewPage = lazy(() =>
  import('@/features/theme-preview').then((m) => ({ default: m.ThemePreviewPage })),
);

/** `/` → the Dashboard when permitted, otherwise the first permitted screen. */
function LandingRedirect() {
  const { can } = usePermissions();
  const path = landingPath(can);
  if (path) return <Navigate to={path} replace />;
  return (
    <PageContainer>
      <EmptyState
        icon={ShieldOff}
        title="No screens are available to your role"
        description="Ask an administrator to review your access."
      />
    </PageContainer>
  );
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorPage />,
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <LandingRedirect /> },

          {
            element: <RequirePermission permission="dashboard.read" />,
            children: [{ path: 'dashboard', element: <DashboardPage /> }],
          },

          {
            element: <RequirePermission permission="catalogue.read" />,
            children: [
              { path: 'brands', element: <BrandsPage /> },
              { path: 'brands/:brandId', element: <BrandDetailsPage /> },
              { path: 'categories', element: <CategoriesPage /> },
              { path: 'regions', element: <RegionsPage /> },
              { path: 'market', element: <MarketPage /> },
            ],
          },

          {
            element: <RequirePermission permission="catalogue.manage" />,
            children: [{ path: 'brands/import', element: <CatalogueImportPage /> }],
          },

          {
            element: <RequirePermission permission="providers.read" />,
            children: [
              { path: 'providers', element: <ProvidersPage /> },
              { path: 'providers/:providerId', element: <ProviderDetailsPage /> },
            ],
          },

          {
            element: <RequirePermission permission="pricing.read" />,
            children: [
              { path: 'countries', element: <CountriesPage /> },
              { path: 'countries/:countryId', element: <CountryPage /> },
              { path: 'currencies', element: <CurrenciesPage /> },
              // Pricing approvals merged into the Approvals inbox; old links land on its pricing filter.
              {
                path: 'pricing/approvals',
                element: <Navigate to="/approvals?type=PricingParameterSet" replace />,
              },
            ],
          },

          {
            element: <RequirePermission permission="pricing.calculation.read" />,
            children: [{ path: 'pricing/calculator', element: <PricingCalculatorPage /> }],
          },

          {
            element: <RequirePermission permission="orders.read" />,
            children: [{ path: 'orders', element: <OrdersPage /> }],
          },

          {
            element: <RequirePermission permission="orders.details.read" />,
            children: [{ path: 'orders/:orderId', element: <OrderDetailsPage /> }],
          },

          {
            // complaints.read alone opens the read-only, limited view (no role holds it).
            element: <RequirePermission permission={['complaints.manage', 'complaints.read']} />,
            children: [
              { path: 'complaints', element: <ComplaintsPage /> },
              { path: 'complaints/:complaintId', element: <ComplaintPage /> },
            ],
          },

          {
            element: <RequirePermission permission="customers.read" />,
            children: [
              { path: 'customers', element: <CustomersPage /> },
              { path: 'customers/limits', element: <PurchaseLimitsPage /> },
              { path: 'customers/:customerId', element: <CustomerPage /> },
            ],
          },

          {
            element: <RequirePermission permission="refunds.request" />,
            children: [{ path: 'refunds', element: <RefundsPage /> }],
          },

          {
            element: <RequirePermission permission="reconciliation.read" />,
            children: [
              { path: 'reconciliation', element: <ReconciliationPage /> },
              { path: 'fulfilment', element: <FulfilmentPage /> },
            ],
          },

          {
            element: <RequirePermission permission="newsletter.read" />,
            children: [{ path: 'newsletter', element: <NewsletterSubscribersPage /> }],
          },

          {
            element: <RequirePermission permission="loyalty.read" />,
            children: [{ path: 'loyalty', element: <LoyaltyConfigurationPage /> }],
          },

          {
            element: <RequirePermission permission="coupons.read" />,
            children: [
              { path: 'coupons', element: <CouponsPage /> },
              { path: 'global-discount', element: <GlobalDiscountPage /> },
            ],
          },

          {
            element: <RequirePermission permission="campaigns.read" />,
            children: [
              { path: 'campaigns', element: <CampaignsPage /> },
              { path: 'campaigns/report', element: <CampaignReportPage /> },
              { path: 'campaigns/:campaignId', element: <CampaignDetailsPage /> },
              { path: 'agencies', element: <AgenciesPage /> },
            ],
          },

          {
            element: <RequirePermission permission="users.manage" />,
            children: [{ path: 'staff', element: <StaffPage /> }],
          },

          {
            element: <RequirePermission permission="reports.operations.read" />,
            children: [{ path: 'reports', element: <ReportsPage /> }],
          },

          {
            element: <RequirePermission permission="reports.partner.read" />,
            children: [{ path: 'partner-report', element: <PartnerReportPage /> }],
          },

          {
            element: <RequirePermission permission="settlements.read" />,
            children: [
              { path: 'settlements', element: <SettlementsPage /> },
              { path: 'settlements/:statementId', element: <SettlementDetailsPage /> },
            ],
          },

          {
            // Issuing needs settlements.issue on top of settlements.read (the nav entry's).
            element: <RequirePermission permission="settlements.read" />,
            children: [
              {
                element: <RequirePermission permission="settlements.issue" />,
                children: [{ path: 'settlements/new', element: <NewSettlementPage /> }],
              },
            ],
          },

          {
            element: <RequirePermission permission="approvals.read" />,
            children: [{ path: 'approvals', element: <ApprovalsInboxPage /> }],
          },

          {
            element: <RequirePermission permission="reports.audit.read" />,
            children: [{ path: 'audit-log', element: <AuditLogPage /> }],
          },

          {
            element: <RequirePermission permission="system.health.read" />,
            children: [{ path: 'system-health', element: <SystemHealthPage /> }],
          },

          // Development-only: a visual regression surface for the brand system.
          // Excluded from production builds by the guard in the page itself.
          { path: 'theme-preview', element: <ThemePreviewPage /> },

          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
